<template>
  <div :class="cn(variants({ size }), classes?.root)">
    <ProgressRoot :value="progress" as-child>
      <svg :class="cn('z-1 relative h-full w-full', classes?.svgRoot)" viewBox="0 0 100 100">
        <!-- Background circle -->
        <path :class="cn('stroke-muted fill-none stroke-[6px]', classes?.track)" :d="trackPath" />
        <!-- Progress circle -->
        <ProgressIndicator as-child>
          <path
            :class="
              cn(
                `stroke-primary fill-none stroke-[6px] transition-[stroke-dasharray,opacity] duration-700 data-[value='0']:opacity-0`,
                classes?.progress
              )
            "
            :d="trackPath"
            :style="{
              'stroke-dasharray': `${dashOffset}px, ${circumference}px`,
              'stroke-dashoffset': '0px',
            }"
          />
        </ProgressIndicator>
      </svg>

      <div :class="cn('absolute inset-0 z-0 flex items-center justify-center', classes?.indicator)">
        <slot>
          <span :class="cn('text-foreground text-lg font-bold', classes?.indicatorText)"
            >{{ progress }}%</span
          >
        </slot>
      </div>
    </ProgressRoot>
  </div>
</template>

<script setup lang="ts">
import { VariantProps, cva } from "class-variance-authority"
import { ProgressIndicator, ProgressRoot } from "reka-ui"

export interface CircularProgressClasses {
  root: ClassValue
  svgRoot: ClassValue
  progress: ClassValue
  track: ClassValue
  indicator: ClassValue
  indicatorText: ClassValue
}

const props = defineProps<{
  size?: VariantProps<typeof variants>["size"]
  progress?: number
  classes?: Partial<CircularProgressClasses>
}>()

const RADIUS = 45
const circumference = 2 * Math.PI * RADIUS

const progress = computed(() => props.progress ?? 0)
const dashOffset = computed(() => (progress.value / 100) * circumference)

const trackPath = computed(() => {
  const r = RADIUS
  return `
          M 50 50
          m 0 -${r}
          a ${r} ${r} 0 1 1 0 ${r * 2}
          a ${r} ${r} 0 1 1 0 -${r * 2}
          `
})

const variants = cva("relative", {
  variants: {
    color: {
      primary: "stroke-primary",
      secondary: "stroke-secondary",
      muted: "stroke-muted",
    },
    strokeWidth: {
      xs: "stroke-2",
      sm: "stroke-2",
      md: "stroke-4",
      lg: "stroke-6",
    },
    size: {
      xs: "size-10",
      sm: "size-14",
      md: "size-20",
      lg: "size-30",
    },
  },

  defaultVariants: {
    size: "md",
  },
})
</script>
